import type {ComboboxData, ComboboxItem, MultiSelectProps, SelectProps} from "@mantine/core";
import {CheckIcon, Combobox, Group, MultiSelect, PillsInput, ScrollArea, Select, useCombobox} from "@mantine/core";
import {useState} from "react";
import {twMerge} from "tailwind-merge";
import ArrowDownIcon from "~/svgs/arrowDownDropdown.svg?react";

export function OutlineDropdown<T>(
    props: SelectProps & {
        items: ComboboxData;
        selectedItem: T | null;
        setSelectedItem: (item: T | null, option: ComboboxItem) => void;
        label?: string;
        placeholder?: string;
        rootClassName?: string;
        dropdownClassName?: string;
        inputClassName?: string;
    },
) {
    const {items, selectedItem, setSelectedItem, label, placeholder, rootClassName, dropdownClassName, inputClassName} = props;

    // Since we are spreading the props directly on select, removing custom attributes
    const selectProps = {
        ...props,
        selectedItem: undefined,
        setSelectedItem: undefined,
        rootClassName: undefined,
        dropdownClassName: undefined,
        inputClassName: undefined,
    };

    return (
        <Select
            {...selectProps}
            data={items}
            value={selectedItem}
            onChange={(item, option) => setSelectedItem(item, option)}
            classNames={{
                root: twMerge("w-fit", rootClassName),
                input: twMerge(
                    "!p-3 !border text-ds-text !border-solid !border-ds-elevation-surface-current hover:!bg-ds-surface-hovered !transition !duration-200 !bg-ds-surface !rounded-sg-small placeholder:!text-ds-text placeholder:!align-middle gj-text-body-300",
                    inputClassName,
                ),
                dropdown: twMerge("!border !border-solid !border-ds-border !bg-ds-surface !p-0 gj-text-body-300", dropdownClassName),
                // option: "hover:bg-opacity-40",
                ...props.classNames,
                // TODO: Add selected item bg-opacity-80
            }}
            className="gj-text-body-300 border-ds-elevation-surface-current"
            placeholder={placeholder}
            label={label}
            width={50}
            rightSection={selectProps.rightSection ?? <ArrowDownIcon className="text-ds-text h-3 w-3 fill-ds-icon" />}
            checkIconPosition="right"
            comboboxProps={{
                transitionProps: {transition: "pop", duration: 200},
                ...selectProps.comboboxProps,
            }}
        />
    );
}

export function OutlineMultipleDropdown(
    props: MultiSelectProps & {
        items: ComboboxData;
        selectedItems: Array<string>;
        setSelectedItems: (item: Array<string>) => void;
        label?: string;
        placeholder?: string;
        rootClassName?: string;
    },
) {
    const {items, selectedItems, setSelectedItems, label, placeholder, rootClassName} = props;

    // Since we are spreading the props directly on select, removing custom attributes
    const selectProps = {
        ...props,
        selectedItems: undefined,
        setSelectedItems: undefined,
        rootClassName: undefined,
    };

    return (
        <>
            <MultiSelect
                {...selectProps}
                data={items}
                value={selectedItems}
                onChange={(items) => {
                    setSelectedItems(items);
                }}
                classNames={{
                    root: twMerge("w-fit", rootClassName),
                    input: "!p-3 !border !border-solid !border-ds-elevation-surface-current hover:!bg-ds-surface-hovered !transition !duration-200 !bg-ds-surface !rounded-sg-small text-ds-text placeholder:!text-ds-text-subtlest placeholder:!align-middle !gj-text-label-400 !text-ds-text",
                    dropdown: twMerge("!border !border-solid !border-ds-border !p-0 gj-text-body-300"),
                    // option: "hover:bg-opacity-40",
                    ...props.classNames,
                    // TODO: Add selected item bg-opacity-80
                }}
                className="gj-text-body-300 text-ds-text"
                placeholder={placeholder}
                label={label}
                width={50}
                rightSection={<ArrowDownIcon className="h-4 w-4 fill-ds-icon" />}
                checkIconPosition="right"
                comboboxProps={{
                    ...selectProps.comboboxProps,
                    transitionProps: {transition: "pop", duration: 200},
                }}
            />
        </>
    );
}

export function OutlineMultipleDropdownWithoutPills({
    items,
    selectedItems,
    setSelectedItems,
    label,
    placeholderLabel,
    rootClassNameLabel,
    dropdownClassName,
}: {
    items: Array<string>;
    selectedItems: Array<string>;
    setSelectedItems: (item: Array<string>) => void;
    label?: string;
    placeholder?: string;
    rootClassName?: string;
    dropdownClassName?: string;
}) {
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
        onDropdownOpen: () => combobox.updateSelectedOptionIndex("active"),
    });

    const [search, setSearch] = useState("");

    const handleValueSelect = (item: string) => {
        if (selectedItems.includes(item) === true) {
            const newItems = structuredClone(selectedItems).filter((currentItem) => currentItem !== item);
            setSelectedItems(newItems);
            return;
        }
        setSelectedItems([...selectedItems, item]);
    };

    const options = items
        .filter((item) => item.toLowerCase().includes(search.trim().toLowerCase()))
        .sort((a, b) => a.localeCompare(b))
        .map((item) => (
            <Combobox.Option
                value={item}
                key={item}
                active={selectedItems.includes(item)}
            >
                <Group gap="sm">
                    {selectedItems.includes(item) ? (
                        <CheckIcon
                            size={12}
                            className="text-ds-icon fill-ds-icon"
                        />
                    ) : null}
                    <span>{item}</span>
                </Group>
            </Combobox.Option>
        ));

    return (
        <>
            <Combobox
                store={combobox}
                onOptionSubmit={handleValueSelect}
                withinPortal={false}
                transitionProps={{
                    transition: "pop-top-left",
                    duration: 200,
                }}
                classNames={{
                    dropdown: "!max-h-[20rem]",
                }}
            >
                <Combobox.DropdownTarget>
                    <PillsInput
                        onClick={() => combobox.openDropdown()}
                        classNames={{
                            input: "!px-3 !py-2 !border !border-solid !border-ds-elevation-surface-current hover:!bg-ds-surface-hovered !transition !duration-200 !bg-transparent !rounded-sg-small placeholder:!text-ds-text-subtlest placeholder:!align-middle gj-text-body-300",
                        }}
                    >
                        <Combobox.EventsTarget>
                            <PillsInput.Field
                                onFocus={() => combobox.openDropdown()}
                                onBlur={() => combobox.closeDropdown()}
                                value={search}
                                placeholder={
                                    selectedItems.length === 0
                                        ? "Select values"
                                        : `${selectedItems.length === 1 ? `${selectedItems.length} value` : `${selectedItems.length} values`} selected`
                                }
                                onChange={(event) => {
                                    combobox.updateSelectedOptionIndex();
                                    setSearch(event.currentTarget.value);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Backspace" && search.length === 0) {
                                        event.preventDefault();
                                    }
                                }}
                            />
                        </Combobox.EventsTarget>
                    </PillsInput>
                </Combobox.DropdownTarget>

                <Combobox.Dropdown
                    classNames={{
                        dropdown: twMerge("!border !border-solid !border-ds-border !bg-ds-surface !p-0 gj-text-body-300", dropdownClassName),
                    }}
                >
                    <ScrollArea.Autosize mah={300}>
                        <Combobox.Options>{options.length > 0 ? options : <Combobox.Empty>Nothing found...</Combobox.Empty>}</Combobox.Options>
                    </ScrollArea.Autosize>
                </Combobox.Dropdown>
            </Combobox>
        </>
    );
}

export function PrimaryDropdown(
    props: SelectProps & {
        items: ComboboxData;
        selectedItem: string | null;
        setSelectedItem: (item: string | null, option: ComboboxItem) => void;
        label?: string;
        placeholder?: string;
    },
) {
    const {items, selectedItem, setSelectedItem, label, placeholder} = props;

    // Since we are spreading the props directly on select, removing custom attributes
    const selectProps = {
        ...props,
        selectedItem: undefined,
        setSelectedItem: undefined,
    };

    return (
        <Select
            {...selectProps}
            data={items}
            value={selectedItem}
            onChange={(item, option) => setSelectedItem(item, option)}
            className="bg-[#0D0D0D] border border-[#666F75] rounded-xl w-full h-full"
            classNames={{
                root: "cursor-pointer",
                wrapper: "w-full h-full py-2 px-2",
                input: "!bg-[#0D0D0D] !border-none !py-0 !cursor-pointer",
                dropdown: "!bg-ds-surface",
                // option: "hover:bg-opacity-40",
            }}
            placeholder={placeholder}
            label={label}
            width={50}
            rightSection={<ArrowDownIcon className="h-4 w-4 fill-ds-icon" />}
            checkIconPosition="right"
            comboboxProps={{
                transitionProps: {transition: "pop", duration: 200},
            }}
        />
    );
}
